/**
 * Demo data for the Landing queue and Environments tabs (?mock=1): two queues, a landing in
 * flight, a parked conflict, a failed verify, recent landings and three environments with a
 * freeze. Mock actions change this state in place, so the tabs behave as they do on a hub.
 */
import { defaultLandingConfig, landingQueueId, versionOf } from '@orgus/protocol';
import type { Deployment, Landing, LandingEvent, LandingProjectConfig, LandingQueue, LandingState, LandingStatus, LandingStage } from '@orgus/protocol';
import { MOCK_IDS } from '../../lib/mock.ts';

const S = 1000;
const MIN = 60 * S;
const H = 60 * MIN;
const M = MOCK_IDS;

function sha(seed: number): string {
  let h = (seed * 2654435761) >>> 0;
  let out = '';
  while (out.length < 40) {
    h = Math.imul(h ^ (h >>> 13), 1274126177) >>> 0;
    out += h.toString(16).padStart(8, '0');
  }
  return out.slice(0, 40);
}

interface Spec {
  n: number;
  project: string;
  branch: string;
  title: string;
  by: string;
  status: LandingStatus;
  stage: LandingStage;
  step: string;
  submittedAgo: number;
  startedAgo?: number;
  endedAgo?: number;
  machineId?: string;
  conflicts?: number;
  attempts?: number;
  extra?: Partial<Landing>;
}

function landing(now: number, s: Spec): Landing {
  const qid = landingQueueId(s.project, 'main');
  const landedSha = s.status === 'landed' ? sha(s.n) : null;
  const ev = (ago: number, stage: LandingStage, text: string, tone: LandingEvent['tone'] = 'info'): LandingEvent => ({ at: now - ago, stage, text, tone, ...(s.machineId ? { machineId: s.machineId } : {}) });
  const events: LandingEvent[] = [ev(s.submittedAgo, 'change', `Submitted by ${s.by}`)];
  if (s.startedAgo != null) {
    events.push(ev(s.startedAgo, 'verify', `Rebasing ${s.branch} onto main in a fresh checkout on studio`));
    events.push(ev(s.startedAgo - 9 * S, 'verify', `Rebased 2 commits onto ${sha(s.n + 100).slice(0, 7)}`, 'ok'));
    events.push(ev(s.startedAgo - 10 * S, 'verify', 'Running pnpm verify on studio'));
  }
  if (s.status === 'landed' && s.endedAgo != null) {
    events.push(ev(s.endedAgo + 40 * S, 'verify', 'Verify passed in 3 min 12 s', 'ok'));
    events.push(ev(s.endedAgo + 30 * S, 'push', `Pushed to main as ${versionOf(landedSha!)}`, 'ok'));
    events.push(ev(s.endedAgo + 2 * S, 'deploy', 'Live in staging', 'ok'));
    events.push(ev(s.endedAgo, 'landed', `Landed on main as ${versionOf(landedSha!)}`, 'ok'));
  }
  return {
    id: `lnd_demo${String(s.n).padStart(3, '0')}`,
    queueId: qid,
    projectId: s.project,
    target: 'main',
    title: s.title,
    source: { branch: s.branch },
    submittedBy: s.by,
    submittedAt: now - s.submittedAgo,
    status: s.status,
    stage: s.stage,
    step: s.step,
    machineId: s.machineId ?? null,
    jobId: s.startedAgo != null ? `j-land-${s.n}` : null,
    attempts: s.attempts ?? (s.startedAgo != null ? 1 : 0),
    conflicts: s.conflicts ?? 0,
    baseSha: s.startedAgo != null ? sha(s.n + 100) : null,
    headSha: s.startedAgo != null ? sha(s.n + 200) : null,
    landedSha,
    version: landedSha ? versionOf(landedSha) : null,
    startedAt: s.startedAgo != null ? now - s.startedAgo : null,
    endedAt: s.endedAgo != null ? now - s.endedAgo : null,
    updatedAt: now - (s.endedAgo ?? 5 * S),
    events,
    ...s.extra,
  };
}

export function createMockLanding(now: number = Date.now()): LandingState {
  const A = M.orgus;
  const B = M.billing;
  const C = M.mobile;
  const D = M.gateway;
  const landings: Landing[] = [
    landing(now, { n: 1, project: A, branch: 'feat/landing-queue', title: 'Landing queue: one per repo and branch', by: 'agent:builder', status: 'active', stage: 'verify', step: 'Running pnpm verify on studio', submittedAgo: 6 * MIN, startedAgo: 2 * MIN + 10 * S, machineId: M.studio }),
    landing(now, { n: 2, project: A, branch: 'fix/sse-reconnect', title: 'Reconnect the stream after sleep', by: 'owner', status: 'queued', stage: 'change', step: 'Waiting for its turn', submittedAgo: 4 * MIN }),
    landing(now, { n: 3, project: A, branch: 'feat/spend-export', title: 'Spend: export CSV', by: 'agent:costs', status: 'queued', stage: 'change', step: 'Waiting for its turn', submittedAgo: 3 * MIN }),
    landing(now, { n: 4, project: A, branch: 'chore/bump-vite', title: 'Bump Vite to 8.3', by: 'agent:deps', status: 'queued', stage: 'change', step: 'Waiting for its turn', submittedAgo: 1 * MIN }),
    landing(now, {
      n: 5, project: A, branch: 'feat/rail-density', title: 'Rail: compact density', by: 'agent:design', status: 'parked', stage: 'verify', step: 'feat/rail-density conflicts with main.', submittedAgo: 40 * MIN, startedAgo: 22 * MIN, endedAgo: 20 * MIN, machineId: M.studio, conflicts: 2,
      extra: {
        jobId: null,
        attempts: 0,
        headSha: null,
        events: [
          { at: now - 40 * MIN, stage: 'change', text: 'Submitted by agent:design', tone: 'info' },
          { at: now - 22 * MIN, stage: 'verify', text: 'Rebasing feat/rail-density onto main in a fresh checkout on studio', tone: 'info', machineId: M.studio },
          { at: now - 22 * MIN + 8 * S, stage: 'verify', text: 'Conflict in apps/web/src/components/shell/Rail.tsx, packages/tokens/tokens.css; trying once more in a fresh checkout', tone: 'warn', machineId: M.studio },
          { at: now - 21 * MIN, stage: 'verify', text: 'Rebasing feat/rail-density onto main in a fresh checkout on studio', tone: 'info', machineId: M.studio },
          { at: now - 20 * MIN, stage: 'verify', text: 'feat/rail-density conflicts with main.', tone: 'warn' },
        ],
        conflictFiles: ['apps/web/src/components/shell/Rail.tsx', 'packages/tokens/tokens.css'],
        problem: { what: 'feat/rail-density conflicts with main.', why: 'It did not rebase cleanly, again. Conflicting files: apps/web/src/components/shell/Rail.tsx, packages/tokens/tokens.css.', next: 'Rebase feat/rail-density onto main, resolve the conflicts, push the branch, then retry.' },
      },
    }),
    landing(now, {
      n: 6, project: A, branch: 'feat/audit-csv', title: 'Audit: export to CSV', by: 'agent:audit', status: 'failed', stage: 'verify', step: 'Verify failed.', submittedAgo: 70 * MIN, startedAgo: 55 * MIN, endedAgo: 51 * MIN, machineId: M.studio,
      extra: { problem: { what: 'Verify failed.', why: 'pnpm verify exited with code 1 on studio.', next: 'Open the log, fix feat/audit-csv, push it, then retry.' }, verifyMs: 4 * MIN },
    }),
    landing(now, { n: 7, project: A, branch: 'feat/needs-you-rank', title: 'Rank Needs you by blocked time', by: 'agent:overview', status: 'landed', stage: 'landed', step: 'Landed on main', submittedAgo: 50 * MIN, startedAgo: 18 * MIN, endedAgo: 14 * MIN, machineId: M.studio }),
    landing(now, { n: 8, project: A, branch: 'fix/token-contrast', title: 'Raise muted text contrast to AA', by: 'owner', status: 'landed', stage: 'landed', step: 'Landed on main', submittedAgo: 90 * MIN, startedAgo: 35 * MIN, endedAgo: 31 * MIN, machineId: M.studio }),
    landing(now, { n: 9, project: A, branch: 'feat/machine-drain', title: 'Machines: drain a node', by: 'agent:fleet', status: 'landed', stage: 'landed', step: 'Landed on main', submittedAgo: 3 * H, startedAgo: 2 * H, endedAgo: 2 * H - 4 * MIN, machineId: M.studio }),
    landing(now, { n: 10, project: B, branch: 'feat/stripe-retry', title: 'Retry failed Stripe webhooks', by: 'agent:billing', status: 'active', stage: 'deploy', step: `Deploying ${versionOf(sha(10))} to staging on mbp-16`, submittedAgo: 12 * MIN, startedAgo: 6 * MIN, machineId: M.mbp, extra: { landedSha: sha(10), version: versionOf(sha(10)) } }),
    landing(now, { n: 11, project: B, branch: 'fix/tax-rounding', title: 'Round tax per line', by: 'owner', status: 'queued', stage: 'change', step: 'Waiting for its turn', submittedAgo: 2 * MIN }),
    landing(now, { n: 12, project: B, branch: 'chore/stripe-18', title: 'Bump stripe to 18.2', by: 'agent:deps', status: 'landed', stage: 'landed', step: 'Landed on main', submittedAgo: 5 * H, startedAgo: 4 * H, endedAgo: 4 * H - 6 * MIN, machineId: M.mbp }),
    landing(now, { n: 13, project: C, branch: 'feat/offline-sync', title: 'Offline sync for drafts', by: 'agent:mobile', status: 'queued', stage: 'change', step: 'Waiting for its turn', submittedAgo: 5 * MIN }),
    landing(now, { n: 14, project: C, branch: 'fix/push-badge', title: 'Clear the push badge on open', by: 'agent:mobile', status: 'landed', stage: 'landed', step: 'Landed on main', submittedAgo: 80 * MIN, startedAgo: 40 * MIN, endedAgo: 36 * MIN, machineId: M.mbp }),
    landing(now, { n: 15, project: D, branch: 'feat/rate-limits', title: 'Per-key rate limits', by: 'owner', status: 'landed', stage: 'landed', step: 'Landed on main', submittedAgo: 3 * H, startedAgo: 2 * H, endedAgo: 2 * H - 5 * MIN, machineId: M.mbp }),
  ];
  const q = (project: string, active: string | null, order: string[], landedCount: number, avgMs: number): LandingQueue => ({
    id: landingQueueId(project, 'main'),
    projectId: project,
    target: 'main',
    paused: false,
    order,
    activeId: active,
    waiting: null,
    avgMs,
    landedCount,
    createdAt: now - 3 * 24 * H,
  });
  const queues = [
    q(A, 'lnd_demo001', ['lnd_demo002', 'lnd_demo003', 'lnd_demo004'], 41, 4 * MIN + 20 * S),
    q(B, 'lnd_demo010', ['lnd_demo011'], 12, 7 * MIN),
    {
      ...q(C, null, ['lnd_demo013'], 8, 5 * MIN),
      waiting: 'Waiting for studio to allow landing. Orgus asked on that computer: someone there clicks Allow, or add another machine.',
      consent: [{ machineId: M.studio, machineName: 'studio', checkoutId: 'co_demo_c', state: 'asking' as const, askedAt: now - 2 * MIN }],
    },
    q(D, null, [], 23, 6 * MIN),
  ];
  const cfgA: LandingProjectConfig = {
    ...defaultLandingConfig(A, 'main', now - 2 * 24 * H),
    verifyCommand: 'pnpm verify',
    deployCommand: 'pnpm deploy --env {env} --version {version}',
    machineLabels: ['primary'],
    updatedBy: 'owner',
  };
  cfgA.environments = cfgA.environments.map((e) =>
    e.name === 'production'
      ? { ...e, freezes: [{ id: 'frz_demo1', reason: 'No production deploys on Friday evenings', createdBy: 'owner', createdAt: now - 7 * 24 * H, weekly: { days: [5], start: '16:00', end: '23:59', tz: Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC' } }] }
      : e,
  );
  const cfgB: LandingProjectConfig = { ...defaultLandingConfig(B, 'main', now - 5 * 24 * H), verifyCommand: 'npm test', deployCommand: './scripts/deploy.sh {env} {version}', updatedBy: 'owner' };
  const dep = (n: number, project: string, env: string, landingN: number, status: Deployment['status'], ago: number, kind: Deployment['kind'] = 'deploy', extra: Partial<Deployment> = {}): Deployment => ({
    id: `dpl_demo${String(n).padStart(3, '0')}`,
    projectId: project,
    env,
    version: versionOf(sha(landingN)),
    sha: sha(landingN),
    landingId: `lnd_demo${String(landingN).padStart(3, '0')}`,
    kind,
    status,
    by: kind === 'deploy' ? 'landing-queue' : 'owner',
    startedAt: now - ago,
    endedAt: status === 'deploying' ? null : now - ago + 70 * S,
    machineId: project === A ? M.studio : M.mbp,
    jobId: `j-dep-${n}`,
    ...extra,
  });
  const deployments: Deployment[] = [
    dep(1, B, 'staging', 10, 'deploying', 40 * S),
    dep(2, A, 'staging', 7, 'live', 14 * MIN),
    dep(3, A, 'preview', 7, 'live', 13 * MIN, 'promote', { fromEnv: 'staging' }),
    dep(4, A, 'staging', 8, 'superseded', 31 * MIN),
    dep(5, A, 'production', 8, 'live', 25 * MIN, 'promote', { fromEnv: 'staging' }),
    dep(6, A, 'staging', 9, 'superseded', 2 * H - 4 * MIN),
    dep(7, A, 'production', 9, 'superseded', 100 * MIN, 'promote', { fromEnv: 'staging' }),
    dep(8, B, 'staging', 12, 'live', 4 * H - 6 * MIN),
    dep(9, B, 'production', 12, 'live', 3 * H, 'promote', { fromEnv: 'staging' }),
  ];
  return { queues, landings, configs: [cfgA, cfgB], deployments, at: now };
}
